import { selMessage } from "@selector/shared";

import { createErrorHandler } from "@/app-utils/errors.ts";
import { type ISelectOption } from "@/app-utils/form.ts";
import {
  type GenomeAssembly,
  type InputGenomeAssembliesInput,
  type InputNcbiParamsInputData,
  type Taxonomy,
} from "@/graphql.ts";
import {
  CHECK_GENOME_IDENTIFIER_FAILURE,
  CHECK_GENOME_IDENTIFIER_REQUEST,
  CHECK_GENOME_IDENTIFIER_SUCCESS,
  CHECK_GENOME_NAME_FAILURE,
  CHECK_GENOME_NAME_REQUEST,
  CHECK_GENOME_NAME_SUCCESS,
  DOWNLOAD_NCBI_GENOMELIST_FAILURE,
  DOWNLOAD_NCBI_GENOMELIST_SUCCESS,
  DOWNLOADING_NCBI_GENOMELIST,
  LOAD_NCBI_TAXONOMIES_FAILIURE,
  LOAD_NCBI_TAXONOMIES_SUCCESS,
  LOADING_NCBI_GENOMEASSEMBLIES,
  LOADING_NCBI_GENOMEASSEMBLIES_FAILURE,
  LOADING_NCBI_GENOMEASSEMBLIES_SUCCESS,
  LOADING_NCBI_TAXONOMIES,
  SET_GENOME_ASSEMBLY_OPTIONS,
  UPDATE_NCBI_STATE,
} from "@/redux/action-types.ts";

import type WebApiClient from "../../api/WebApiClient.js";
import { notify } from "../../modules/notifications/notify.js";
import { createReducer } from "../redux-utils.js";

export interface INCBIDownloadOptions {
  identifier: string;
  name: string;
  strain: string;
  species: string;
  database: string;
  taxonomy: ISelectOption;
  kingdom: ISelectOption;
  assembly: string;
  groups: ISelectOption[];
}

export interface INCBIAgentPlaybookState {
  taxonomies: Taxonomy[];
  selectedGenomeIds?: number[];
  selectedGenomes?: GenomeAssembly[];
  downloadParamLookup: Record<string, INCBIDownloadOptions>; // map by accession
  loading: boolean;
  loadingAssemblies: boolean;
  downloadingGenomeAssemblyList: boolean;
  lastSelectedGroups: ISelectOption[]; // for sustaining form values across back and forth
  lastSelectedKingdom: ISelectOption; // for sustaining form values across back and forth
  lastSelectedTaxonomy: ISelectOption; // for sustaining form values across back and forth
}

const initialState = {
  updating: false,
  loading: false,
  downloadingGenomeList: false,
  isUniqueGenomeName: true,
  downloadList: [],
};

export default createReducer(initialState, {
  [LOADING_NCBI_GENOMEASSEMBLIES]: (state: INCBIAgentPlaybookState): INCBIAgentPlaybookState => {
    return { ...state, loadingAssemblies: true };
  },

  [LOADING_NCBI_GENOMEASSEMBLIES_SUCCESS]: (
    state: INCBIAgentPlaybookState,
    payload: GenomeAssembly[],
  ): INCBIAgentPlaybookState => {
    return { ...state, selectedGenomes: payload, loadingAssemblies: false };
  },

  [LOADING_NCBI_GENOMEASSEMBLIES_FAILURE]: (state: INCBIAgentPlaybookState): INCBIAgentPlaybookState => {
    return { ...state, loadingAssemblies: false };
  },

  [UPDATE_NCBI_STATE]: (
    state: INCBIAgentPlaybookState,
    payload: Partial<INCBIAgentPlaybookState>,
  ): INCBIAgentPlaybookState => {
    return { ...state, ...payload };
  },

  [SET_GENOME_ASSEMBLY_OPTIONS]: (
    state: INCBIAgentPlaybookState,
    payload: { assemblyAccession: string; options: INCBIDownloadOptions },
  ): INCBIAgentPlaybookState => {
    const { assemblyAccession, options } = payload;
    return {
      ...state,
      downloadParamLookup: {
        ...state.downloadParamLookup,
        [assemblyAccession]: options,
      },
      lastSelectedKingdom: options.kingdom,
      lastSelectedTaxonomy: options.taxonomy,
      lastSelectedGroups: options.groups,
    };
  },

  [LOADING_NCBI_TAXONOMIES]: (state: INCBIAgentPlaybookState, _payload: any): INCBIAgentPlaybookState => {
    return { ...state, loading: true };
  },

  [LOAD_NCBI_TAXONOMIES_SUCCESS]: (state: INCBIAgentPlaybookState, payload: Taxonomy[]): INCBIAgentPlaybookState => {
    return { ...state, taxonomies: payload, loading: false };
  },

  [DOWNLOADING_NCBI_GENOMELIST]: (state: INCBIAgentPlaybookState): INCBIAgentPlaybookState => {
    return { ...state, downloadingGenomeAssemblyList: true };
  },

  [DOWNLOAD_NCBI_GENOMELIST_SUCCESS]: (state: INCBIAgentPlaybookState): INCBIAgentPlaybookState => {
    return { ...state, downloadingGenomeAssemblyList: false };
  },

  [DOWNLOAD_NCBI_GENOMELIST_FAILURE]: (state: INCBIAgentPlaybookState): INCBIAgentPlaybookState => {
    return { ...state, downloadingGenomeAssemblyList: false };
  },

  [CHECK_GENOME_NAME_REQUEST]: (state: INCBIAgentPlaybookState): INCBIAgentPlaybookState => {
    return { ...state, loading: true };
  },

  [CHECK_GENOME_IDENTIFIER_REQUEST]: (state: INCBIAgentPlaybookState): INCBIAgentPlaybookState => {
    return { ...state, loading: true };
  },

  [CHECK_GENOME_NAME_SUCCESS]: (state: INCBIAgentPlaybookState, _ok: boolean): INCBIAgentPlaybookState => {
    return { ...state, loading: false };
  },

  [CHECK_GENOME_IDENTIFIER_SUCCESS]: (state: INCBIAgentPlaybookState, _ok: boolean): INCBIAgentPlaybookState => {
    return { ...state, loading: false };
  },

  [CHECK_GENOME_NAME_FAILURE]: (state: INCBIAgentPlaybookState): INCBIAgentPlaybookState => {
    return { ...state, loading: false };
  },
});

export function installGenomes(optionsArray: InputNcbiParamsInputData[]) {
  return (_dispatch, _getState, webApiClient: WebApiClient) => {
    const jobInput: InputGenomeAssembliesInput = {
      assemblies: optionsArray,
    };
    return webApiClient.installNCBIGenomes(jobInput).then((success) => {
      notify.info({
        title: selMessage({ id: "admin.ncbi.download.started.successTitle" }),
        children: selMessage({ id: "admin.ncbi.download.started.successMessage" }),
      });
      return success;
    });
  };
}

export function loadTaxonomyList() {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    dispatch({ type: LOADING_NCBI_TAXONOMIES });

    return webApiClient
      .fetchAllTaxonomies()
      .then((taxonomies: Taxonomy[]) => {
        dispatch({
          type: LOAD_NCBI_TAXONOMIES_SUCCESS,
          payload: taxonomies,
        });
        return taxonomies;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_NCBI_TAXONOMIES_FAILIURE,
          alertTitle: "Fetching of Taxonomies Failed",
        }),
      );
  };
}

export function loadGenomeAssemblies(ids: number[]) {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    dispatch({ type: LOADING_NCBI_GENOMEASSEMBLIES });
    return webApiClient
      .fetchNCBIGenomesAssemblies(ids)
      .then((result: GenomeAssembly[]) => {
        dispatch({
          type: LOADING_NCBI_GENOMEASSEMBLIES_SUCCESS,
          payload: result,
        });
        return result;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOADING_NCBI_GENOMEASSEMBLIES_FAILURE,
          showAlert: true,
          alertTitle: "Downloading Genome List Failed",
        }),
      );
  };
}

export function downloadGenomeList(withStats: boolean) {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    dispatch({ type: DOWNLOADING_NCBI_GENOMELIST });
    notify.info({
      title: selMessage({ id: "admin.ncbi.update.genomeListing.infoTitle" }),
      children: selMessage({ id: "admin.ncbi.update.genomeListing.started.infoMessage" }),
    });
    return webApiClient
      .downloadNCBIGenomeList(withStats)
      .then((result: boolean) => {
        notify.info({
          title: selMessage({ id: "admin.ncbi.update.genomeListing.infoTitle" }),
          children: selMessage({ id: "admin.ncbi.update.genomeListing.finished.infoMessage" }),
        });
        dispatch({
          type: DOWNLOAD_NCBI_GENOMELIST_SUCCESS,
          payload: result,
        });
        return result;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: DOWNLOAD_NCBI_GENOMELIST_FAILURE,
          showAlert: true,
          alertTitle: "Downloading Genome List Failed",
        }),
      );
  };
}

export function checkGenomeName(name: string) {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    dispatch({ type: CHECK_GENOME_NAME_REQUEST });

    return webApiClient
      .isValidGenomeName(name)
      .then((unique: boolean) => {
        dispatch({
          type: CHECK_GENOME_NAME_SUCCESS,
          payload: unique,
        });
        return unique;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: CHECK_GENOME_NAME_FAILURE,
          alertTitle: "Checking for Genome Name Failed",
          showAlert: false,
          throwError: true,
        }),
      );
  };
}
export function checkGenomeIdentifier(identifier: string) {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    dispatch({ type: CHECK_GENOME_IDENTIFIER_REQUEST });

    return webApiClient
      .isValidGenomeIdentifier(identifier)
      .then((unique: boolean) => {
        dispatch({
          type: CHECK_GENOME_IDENTIFIER_SUCCESS,
          payload: unique,
        });
        return unique;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: CHECK_GENOME_IDENTIFIER_FAILURE,
          alertTitle: "Checking for Genome Identifier Failed",
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function initNCBIState() {
  return {
    type: UPDATE_NCBI_STATE,
    payload: {
      selectedGenomeIds: [],
      selectedGenomes: [],
      downloadParamLookup: {},
    },
  };
}

export function selectGenomeAssemblies(selectedGenomeIds: number[]) {
  return {
    type: UPDATE_NCBI_STATE,
    payload: {
      selectedGenomeIds: selectedGenomeIds,
      selectedGenomes: [],
    },
  };
}

export function submitGenomeAssemblyConfiguration(assemblyAccession: string, options: INCBIDownloadOptions) {
  return {
    type: SET_GENOME_ASSEMBLY_OPTIONS,
    payload: {
      assemblyAccession: assemblyAccession,
      options: options,
    },
  };
}
